import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { authService } from '../../services/auth/authService';
import { patientsService, PatientMealPlan } from '../../services/patients/patientsService';
import DietPlanLayout from './DietPlanLayout';
import PatientMealPlanTab from './PatientMealPlanTab';
import { SECTION_LABELS } from '../../components/layout/DashboardSidebar/sidebarItems';

/** Meal plans list. Route: /patients/:patientId/diet-plan/:goalId/daily-meals */
const MealPlanListPage: React.FC = () => {
  const { patientId, goalId } = useParams<{ patientId: string; goalId: string }>();
  const [mealPlan, setMealPlan] = useState<PatientMealPlan | null>(null);
  const [patientName, setPatientName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDietPlan = useCallback(async () => {
    if (!patientId || !goalId) {
      setError('Invalid patient or diet plan identifier');
      setLoading(false);
      return;
    }
    const nPatientId = Number(patientId);
    const nGoalId = Number(goalId);
    if (Number.isNaN(nPatientId) || Number.isNaN(nGoalId)) {
      setError('Invalid patient or diet plan identifier');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await patientsService.getPatientMealPlanByGoalId(nPatientId, nGoalId);
      if (!data) {
        setError('Diet plan not found');
        return;
      }
      setMealPlan(data);
      try {
        const patients = await patientsService.getPatients();
        setPatientName(patients.find((p) => p.id === nPatientId)?.name ?? null);
      } catch {
        setPatientName(null);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load diet plan');
    } finally {
      setLoading(false);
    }
  }, [patientId, goalId]);

  useEffect(() => {
    loadDietPlan();
  }, [loadDietPlan]);

  if (!authService.isAuthenticated()) return null;

  const breadcrumbSegments = [{ label: SECTION_LABELS['daily-meals'] ?? 'Meal Plans' }];
  const pageTitle = SECTION_LABELS['daily-meals'] ?? 'Meal Plans';

  return (
    <DietPlanLayout
      patientId={patientId!}
      goalId={goalId!}
      patientName={patientName}
      mealPlan={mealPlan}
      breadcrumbSegments={breadcrumbSegments}
      pageTitle={pageTitle}
      loading={loading}
      error={error}
    >
      <div style={{ marginTop: 24, marginBottom: 24, padding: 20, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12 }}>
        <PatientMealPlanTab
          patientId={Number(patientId)}
          goalId={goalId ? Number(goalId) : undefined}
          mealPlanViewMode="list"
        />
      </div>
    </DietPlanLayout>
  );
};

export default MealPlanListPage;
